import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'

import { ReactComponent as LockIcon } from './../../../assets/images/icons/i-lock.svg'
import { ReactComponent as UnlockIcon } from './../../../assets/images/icons/i-unlock.svg'
import Button from '../../Common/Buttons/Button/Button'
import Modal from '../../Common/Modal/Modal'
import Checkbox from '../../Common/Checkbox/Checkbox'
import InputNumeric from '../../Common/InputNumeric/InputNumeric'

import {
  updateResizeValues,
  toggleResizeRatioLock,
  setResizeChangedStatus,
  setResizeModal,
} from '../../../store/actions/resize.actions'
import { addNewSetting, updateSettingsIds } from '../../../store/actions/settings.actions'
import {
  getLastPhysicsSetting,
  convertPercentToValue,
  convertValueToPercent,
} from '../../../helpers/util'
import { sendMetric } from '../../../helpers/metric'
import IconButton from '../../Common/Buttons/IconButton/IconButton'

import './ResizeControl.scss'

class ResizeControl extends Component {
  static propTypes = {
    resize: PropTypes.object.isRequired,
    showResizeModal: PropTypes.bool.isRequired,
    setResizeModal: PropTypes.func.isRequired,
    resizeTo: PropTypes.string.isRequired,
    canvas: PropTypes.object.isRequired,
    imageSrc: PropTypes.string.isRequired,
    imageData: PropTypes.object.isRequired,
    settingsList: PropTypes.array.isRequired,
    currentSettingId: PropTypes.number.isRequired,
    addNewSetting: PropTypes.func.isRequired,
    updateResizeValues: PropTypes.func.isRequired,
    toggleResizeRatioLock: PropTypes.func.isRequired,
    setResizeChangedStatus: PropTypes.func.isRequired,
    updateSettingsIds: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    history: PropTypes.any,
  }

  state = {
    resetSettingId: 0
  }

  componentDidMount() {
    const { settingsList, currentSettingId } = this.props
    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)

    if (lastSetting) this.setState({ resetSettingId: lastSetting.id })
    this.initResize()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentSettingId !== this.props.currentSettingId) {
      this.initResize()
    }
  }

  componentWillUnmount() {
    const { resize, updateResizeValues, setResizeChangedStatus } = this.props

    const newResize = {
      ...resize,
      id: 0,
    }

    updateResizeValues(newResize)
    setResizeChangedStatus(false)
  }

  initResize = () => {
    const { imageData, resize, settingsList, currentSettingId, updateResizeValues } = this.props

    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    let data = imageData

    if (lastSetting) {
      data = lastSetting.imageData
    }

    const newResize = {
      ...resize,
      width: resize.isPercentage ? convertValueToPercent(data.width, data.width) : data.width,
      height: resize.isPercentage ? convertValueToPercent(data.height, data.height) : data.height,
    }

    updateResizeValues(newResize)
  }

  toggleResizeLock = () => {
    const { resize, toggleResizeRatioLock } = this.props

    toggleResizeRatioLock(!resize.locked)
  }

  onSizeChange = (value, type) => {
    const {
      resize,
      updateResizeValues,
      setResizeChangedStatus,
      settingsList,
      currentSettingId,
      imageData
    } = this.props

    const secondType = type === 'width' ? 'height' : 'width'

    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    let data = imageData

    if (lastSetting) {
      data = lastSetting.imageData
    }

    let maxValue = 10000
    const maxWidthPercentValue = maxValue / data.width * 100
    const maxHeightPercentValue = maxValue / data.height * 100

    if (resize.isPercentage) {
      maxValue = type === 'width' ? maxWidthPercentValue : maxHeightPercentValue
    }

    value = value === 0 ? 1 : Math.min(value, maxValue)
    const newResize = {
      ...resize,
      [type]: value,
    }

    if (resize.locked) {
      if (resize.isPercentage) {
        maxValue = Math.min(maxWidthPercentValue, maxHeightPercentValue)
      }
      const ratio = resize.width / resize.height
      newResize[secondType] = secondType === 'height' ? value / ratio : value * ratio
      if (newResize[secondType] > maxValue) {
        newResize[secondType] = maxValue
        newResize[type] = type === 'height' ? newResize[secondType] / ratio : newResize[secondType] * ratio
      }
    }

    updateResizeValues(newResize)
    setResizeChangedStatus(true)
  }

  onApply = (e, redirect) => {
    const {
      settingsList,
      resize,
      addNewSetting,
      updateSettingsIds,
      imageData,
      currentSettingId,
      updateResizeValues,
      setResizeChangedStatus,
      setResizeModal,
      resizeTo,
    } = this.props
    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    const data = lastSetting ? lastSetting.imageData : imageData

    const resizeWidth = resize.isPercentage ? convertPercentToValue(resize.width, data.width) : resize.width
    const resizeHeight = resize.isPercentage? convertPercentToValue(resize.height, data.height) : resize.height
    const shadowCanvas = document.createElement('canvas')
    const renderer = document.createElement('canvas')

    shadowCanvas.width = data.width
    shadowCanvas.height = data.height
    renderer.width = resizeWidth
    renderer.height = resizeHeight

    const context = shadowCanvas.getContext('2d')
    const rendererContext = renderer.getContext('2d')

    context.putImageData(data, 0, 0)

    rendererContext.drawImage(shadowCanvas, 0, 0, resizeWidth, resizeHeight)

    const newImageData = rendererContext.getImageData(0, 0, resizeWidth, resizeHeight)

    const event = {
      event: 'RESIZE_APPLIED',
      gtm: {
        eventCategory: 'Interactions',
        eventAction: 'click',
        eventLabel: 'RESIZE_APPLIED'
      },
      amplitude: {
        SAVE_PROPORTION: resize.locked ? 'YES' : 'NO'
      }
    }

    sendMetric(event)

    updateSettingsIds(currentSettingId + 1)

    addNewSetting({
      name: 'resize',
      isCommitted: true,
      id: currentSettingId + 1,
      imageData: newImageData,
    })

    const newResize = {
      ...resize,
      id: currentSettingId + 1,
    }

    updateResizeValues(newResize)
    setResizeChangedStatus(false)

    setResizeModal(false)

    if (redirect) this.props.history.push(resizeTo)
  }

  formatLabel = (label) => {
    const { resize } = this.props

    return label + (resize.isPercentage ? ' %' : '')
  }

  onCloseModal = (e, redirect) => {
    const { resizeTo, setResizeModal } = this.props

    setResizeModal(false)

    if (redirect) this.props.history.push(resizeTo)
  }

  onPercentageCheckboxChange = () => {
    const { resize, imageData, settingsList, currentSettingId, updateResizeValues } = this.props
    const isPercentage = !resize.isPercentage
    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    let data = imageData

    if (lastSetting) {
      data = lastSetting.imageData
    }

    const newResize = {
      ...resize,
      isPercentage,
      width: isPercentage ?
        convertValueToPercent(resize.width, data.width) :
        convertPercentToValue(resize.width, data.width),
      height: isPercentage ?
        convertValueToPercent(resize.height, data.height) :
        convertPercentToValue(resize.height, data.height),
    }

    updateResizeValues(newResize)
  }

  render() {
    const { 
      resize, 
      imageSrc, 
      showResizeModal, 
      t, 
      imageData 
    } = this.props

    const maxValue = 10000

    const maxWidthPercentValue = maxValue / imageData.width * 100
    const maxHeightPercentValue = maxValue / imageData.height * 100

    return (
      <div className="resize-control">
        <div className="resize-control__title">
          {t('titles.resize')}
        </div>
        <div className="resize-control__size">
          <label className="resize-control__label" htmlFor="resize-width">
            {this.formatLabel(t('controls.width'))}
          </label>
          <label className="resize-control__label" htmlFor="resize-height">
            {this.formatLabel(t('controls.height'))}
          </label>
          <InputNumeric 
            className="input-text"
            id="resize-width"
            value={resize.width.toFixed()}
            onChange={(value) => this.onSizeChange(value, 'width')}
            min={1}
            max={resize.isPercentage ? maxWidthPercentValue : maxValue}
            strict
          />
          <IconButton
            onClick={this.toggleResizeLock}
          >
            {resize.locked ?
              <LockIcon /> :
              <UnlockIcon />
            }
          </IconButton>
          <InputNumeric 
            className="input-text"
            id="resize-height"
            value={resize.height.toFixed()}
            onChange={(value) => this.onSizeChange(value, 'height')}
            min={1}
            max={resize.isPercentage ? maxHeightPercentValue : maxValue}
            strict
          />
        </div>
        <Checkbox
          label={t('toggles.use-percentage')}
          value={resize.isPercentage}
          onChange={this.onPercentageCheckboxChange}
        />
        <div className="resize-control__buttons">
          <Button
            disabled={!imageSrc || !resize.changed}
            block
            onClick={this.onApply}
          >
            {t('buttons.apply')}
          </Button>
        </div>
        <Modal isOpen={showResizeModal} onCloseModal={this.onCloseModal}>
          <div className="modal__body">{t('modals.image-changes')}</div>
          <div className="modal__buttons">
            <Button variant="ghost" onClick={(e) => this.onCloseModal(e, true)}>
              {t('buttons.no')}
            </Button>
            <div className="modal__buttons-separator" />
            <Button onClick={(e) => this.onApply(e, true)}>
              {t('buttons.yes')}
            </Button>
          </div>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = ({
  imageStore: { imageSrc, imageData },
  canvasStore: { canvas },
  resizeStore: { resize, showResizeModal, resizeTo },
  settingsStore: { settingsList, currentSettingId },
}) => ({
  imageSrc,
  imageData,
  canvas,
  resize,
  showResizeModal,
  resizeTo,
  settingsList,
  currentSettingId,
})
const mapDispatchToProps = (dispatch) => ({
  updateResizeValues: (values) => dispatch(updateResizeValues(values)),
  toggleResizeRatioLock: (locked) => dispatch(toggleResizeRatioLock(locked)),
  setResizeChangedStatus: (status) => dispatch(setResizeChangedStatus(status)),
  updateSettingsIds: (id) => dispatch(updateSettingsIds(id)),
  addNewSetting: (setting) => dispatch(addNewSetting(setting)),
  setResizeModal: (status) => dispatch(setResizeModal(status)),
})

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(ResizeControl)
)
